/**
 * CSRF state for the Google sign-in redirect.
 *
 * The backend issues a random `state` alongside the authorization URL. We hold
 * it here until Google redirects back, then refuse the callback unless the
 * value returned matches. sessionStorage is the right home for it: it is scoped
 * to this origin and this tab, and a full-page redirect out to Google and back
 * stays in the same tab, so it survives the round trip. An attacker who starts
 * their own flow cannot write to it — which is what stops them handing a victim
 * an authorization code and silently signing that victim into their account.
 *
 * Reads and writes are wrapped because sessionStorage throws rather than
 * returning null in some privacy modes.
 */

export const OAUTH_STATE_KEY = 'google_oauth_state';

export function rememberOauthState(state) {
  if (!state) return;
  try {
    window.sessionStorage.setItem(OAUTH_STATE_KEY, state);
  } catch (e) { /* ignore */ }
}

/** Read the stored state and clear it. One-time use: a state cannot be replayed. */
export function consumeOauthState() {
  try {
    const state = window.sessionStorage.getItem(OAUTH_STATE_KEY);
    window.sessionStorage.removeItem(OAUTH_STATE_KEY);
    return state;
  } catch (e) {
    return null;
  }
}

/**
 * True only if `stateFromUrl` is the state we issued for this attempt.
 * Always consumes the stored value, so a failed check cannot be retried.
 */
export function isOauthStateValid(stateFromUrl) {
  const expected = consumeOauthState();
  // Both absent must not read as a match.
  if (!expected || !stateFromUrl) return false;
  return expected === stateFromUrl;
}
